import { safeLocalStorage, safeSessionStorage } from './safeStorage';

export const APP_LANGUAGES = ['fr', 'ar', 'en', 'darija', 'darija_ar'] as const;

export type AppLanguage = typeof APP_LANGUAGES[number];
export type UrlLanguage = AppLanguage;

const URL_PREFIX_BY_LANGUAGE: Record<AppLanguage, string> = {
  fr: '',
  ar: '/ar',
  en: '/en',
  darija: '/darija',
  darija_ar: '/darija-ar',
};

const URL_LANGUAGE_BY_PREFIX: Record<string, AppLanguage> = {
  ar: 'ar',
  en: 'en',
  darija: 'darija',
  'darija-ar': 'darija_ar',
};

const LANGUAGE_PREFIX_PATTERN = /^\/(ar|en|darija|darija-ar)(?=\/|$)/;

const LOCALIZED_PUBLIC_ROOTS = new Set([
  'services',
  'pro',
  'providers',
  'terms',
  'privacy',
  'delete-account',
  'contact',
  'how-it-works',
  'security',
  'blog',
  'simulateur-prix',
  'devenir-prestataire',
  'guide',
  'guides',
  'search',
]);

const isAppLanguage = (language: string | null): language is AppLanguage =>
  language !== null && APP_LANGUAGES.includes(language as AppLanguage);

export const getUrlLanguage = (pathname: string): UrlLanguage => {
  const prefix = pathname.match(LANGUAGE_PREFIX_PATTERN)?.[1];
  return prefix ? URL_LANGUAGE_BY_PREFIX[prefix] : 'fr';
};

export const getLanguagePrefix = (language: AppLanguage): string => URL_PREFIX_BY_LANGUAGE[language];

export const localizePath = (pathname: string, language: AppLanguage): string => {
  const cleanPath = pathname.replace(LANGUAGE_PREFIX_PATTERN, '') || '/';
  const prefix = getLanguagePrefix(language);
  return prefix ? `${prefix}${cleanPath === '/' ? '' : cleanPath}` : cleanPath;
};

export const isLocalizedPublicPath = (pathname: string): boolean => {
  const cleanPath = pathname.replace(LANGUAGE_PREFIX_PATTERN, '') || '/';
  if (cleanPath === '/') return true;
  const root = cleanPath.split('/').filter(Boolean)[0];
  return !!root && LOCALIZED_PUBLIC_ROOTS.has(root);
};

export const getStoredLanguage = (): AppLanguage | null => {
  if (typeof window === 'undefined') return null;
  const sessionLanguage = safeSessionStorage.getItem('language');
  if (isAppLanguage(sessionLanguage)) return sessionLanguage;
  const persistentLanguage = safeLocalStorage.getItem('language');
  return isAppLanguage(persistentLanguage) ? persistentLanguage : null;
};

export const persistLanguage = (language: AppLanguage): void => {
  if (typeof window === 'undefined') return;
  safeSessionStorage.setItem('language', language);
  // Keep the existing persistent preference as a fallback for the next browser session.
  safeLocalStorage.setItem('language', language);
};

export const resolveLanguage = (pathname: string, storedLanguage: AppLanguage | null): AppLanguage => {
  if (!isLocalizedPublicPath(pathname)) return storedLanguage ?? 'fr';

  return getUrlLanguage(pathname);
};

export const normalizeLanguage = (language: string): AppLanguage =>
  isAppLanguage(language) ? language : 'fr';
